// src/features/inventory/pages/ItemListPage.jsx
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Percent as PercentIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useItemFiltering } from '../hooks/useItemFiltering';
import { useInventoryItems } from '../hooks/useInventoryItems';
import { useProductGroups } from '../hooks/useProductGroups';
import { useSuppliers } from '../hooks/useSuppliers';
import {
  useInventoryCostPermissions,
  useInventoryItemPermissions,
  useInventorySupplierPermissions,
  useProductGroupPermissions,
} from '../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';

import PageHeader from '../../../components/common/PageHeader';
import InventoryIcon from '@mui/icons-material/Inventory';
import { ExplainTooltip, withInfo } from '../../../components/common/InfoTip';
import HazardDot from '../components/HazardDot';
import PriceAdjustmentDialog from '../components/PriceAdjustmentDialog';

const VALID_ACTIVE_VALUES = ['all', 'active', 'inactive'];

export default function ItemListPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete } = useInventoryItemPermissions();
  const { canRead: canReadGroups } = useProductGroupPermissions();
  const { canRead: canReadSuppliers } = useInventorySupplierPermissions();
  const { canRead: canReadCost, canUpdate: canUpdateCost } = useInventoryCostPermissions();
  // The dialog lists groups and previews from the item values
  const canAdjustPrices = canReadCost && canUpdateCost && canReadGroups;
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);

  // ── Filter state (seeded from URL) ────────────────────────────────────────

  const initialActive = searchParams.get('active') || 'all';

  const {
    items,
    totalCount,
    loading,
    error,
    clearError,
    searchTerm,
    setSearchTerm,
    productGroupId,
    setProductGroupId,
    supplierId,
    setSupplierId,
    activeStatus,
    setActiveStatus,
    hasActiveFilters,
    clearFilters,
    refreshData,
  } = useItemFiltering({
    searchTerm: searchParams.get('search') || '',
    productGroupId: searchParams.get('group') || '',
    supplierId: searchParams.get('supplier') || '',
    activeStatus: VALID_ACTIVE_VALUES.includes(initialActive) ? initialActive : 'all',
  });

  // ── Dropdowns data ─────────────────────────────────────────────────────────

  const { updateItem, deleteItem } = useInventoryItems();
  const { productGroups, fetchProductGroups } = useProductGroups();
  const { suppliers, fetchSuppliers } = useSuppliers();

  useEffect(() => {
    if (canReadGroups) fetchProductGroups();
    if (canReadSuppliers) fetchSuppliers();
  }, [fetchProductGroups, fetchSuppliers, canReadGroups, canReadSuppliers]);

  // ── Sync filter state → URL ────────────────────────────────────────────────

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (productGroupId) params.set('group', productGroupId);
    if (supplierId) params.set('supplier', supplierId);
    if (activeStatus !== 'all') params.set('active', activeStatus);
    setSearchParams(params, { replace: true });
  }, [searchTerm, productGroupId, supplierId, activeStatus, setSearchParams]);

  // ── Lookup maps ────────────────────────────────────────────────────────────

  const groupMap = useMemo(
    () => Object.fromEntries(productGroups.map((g) => [g.product_group_id, g])),
    [productGroups]
  );

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleToggleActive = useCallback(
    async (e, row) => {
      e.stopPropagation();
      if (!canUpdate) return;
      const result = await updateItem(row.item_id, { active: !row.active });
      if (result.success) {
        refreshData();
      } else {
        showError(result.error);
      }
    },
    [canUpdate, updateItem, showError, refreshData]
  );

  const handleDelete = useCallback(
    (e, row) => {
      e.stopPropagation();
      showConfirmDialog({
        title: t('inventory:items.confirm.delete_title'),
        message: t('inventory:items.confirm.delete_message', { name: row.name }),
        severity: 'error',
        onConfirm: async () => {
          const result = await deleteItem(row.item_id);
          if (result.success) {
            showSuccess(t('inventory:items.success.deleted'));
            refreshData();
          } else {
            showError(result.error);
          }
        },
      });
    },
    [showConfirmDialog, deleteItem, showSuccess, showError, t, refreshData]
  );

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = useMemo(
    () => [
      {
        field: 'sku',
        headerName: t('inventory:items.grid.sku'),
        width: 150,
        renderCell: (params) => (
          <Chip label={params.value} size="small" variant="outlined" color="info" />
        ),
      },
      {
        field: 'name',
        headerName: t('inventory:items.grid.name'),
        flex: 1,
        minWidth: 150,
        // A fixed slot for the hazard dot keeps every name lined up
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 10, flexShrink: 0, display: 'flex' }}>
              <HazardDot level={params.row.hazard_level} />
            </Box>
            {params.value}
          </Box>
        ),
      },
      withInfo({
        field: 'last_stock_transaction',
        headerName: t('inventory:items.detail.current_stock'),
        width: 170,
        sortable: false,
        // The unit rides along with the number, so no separate unit column
        renderCell: (params) => {
          const tx = params.row.last_stock_transaction;
          if (!tx) return <Typography variant="body2" color="text.secondary">—</Typography>;
          return (
            <Typography variant="body2" color={tx.quantity_after <= 0 ? 'error.main' : undefined}>
              {tx.quantity_after} {params.row.uom}
            </Typography>
          );
        },
      }, t('inventory:help.items.stock')),
      ...(canReadGroups ? [{
        field: 'product_group_id',
        headerName: t('inventory:items.grid.product_group'),
        width: 200,
        sortable: false,
        renderCell: (params) => {
          const group = groupMap[params.value];
          if (!group) return <Typography variant="body2" color="text.secondary">-</Typography>;
          return <Chip label={group.name} title={`${group.code} · ${group.name}`} size="small" variant="outlined" color='primary' sx={{ maxWidth: '100%' }} />;
        },
      }] : []),
      withInfo({
        field: 'active',
        headerName: t('inventory:items.grid.active'),
        width: 120,
        sortable: false,
        renderCell: (params) => (
          // The chip is also the switch, which nothing else on it says
          <ExplainTooltip title={canUpdate ? t('inventory:help.flags.active_toggle') : ''}>
            <Chip
              label={params.row.active ? t('common:status.active') : t('common:status.inactive')}
              color={params.row.active ? 'success' : 'default'}
              size="small"
              onClick={canUpdate ? (e) => handleToggleActive(e, params.row) : undefined}
              sx={canUpdate ? { cursor: 'pointer' } : undefined}
            />
          </ExplainTooltip>
        ),
      }, t('inventory:help.items.status')),
      {
        field: 'actions',
        renderHeader: () => <></>,
        width: 52,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          canDelete ? (
            <IconButton
              size="small"
              onClick={(e) => handleDelete(e, params.row)}
              title={t('common:buttons.delete')}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          ) : null,
      },
    ],
    [t, canUpdate, canDelete, canReadGroups, groupMap, handleToggleActive, handleDelete]
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">

      {/* Header */}
      <PageHeader
        icon={InventoryIcon}
        title={t('inventory:items.title')}
        subtitle={t('inventory:items.subtitle')}
        actions={<>
          <Tooltip title={t('common:buttons.refresh')}>
            <IconButton onClick={refreshData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canAdjustPrices && (
            <Button
              variant="outlined"
              startIcon={<PercentIcon />}
              onClick={() => setPriceDialogOpen(true)}
            >
              {t('inventory:items.buttons.adjust_prices')}
            </Button>
          )}
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('create')}
            >
              {t('inventory:items.buttons.add')}
            </Button>
          )}
        </>}
      />

      {/* Filter bar */}
      <Box mb={3} display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t('inventory:items.filter.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {canReadGroups && <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="item-list-page-product-group-label">{t('inventory:items.grid.product_group')}</InputLabel>
          <Select labelId="item-list-page-product-group-label"
            value={productGroupId}
            onChange={(e) => setProductGroupId(e.target.value)}
            label={t('inventory:items.grid.product_group')}
          >
            <MenuItem value="">{t('inventory:items.filter.all_groups')}</MenuItem>
            {productGroups.map((g) => (
              <MenuItem key={g.product_group_id} value={g.product_group_id}>
                {g.code} · {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>}

        {canReadSuppliers && <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="item-list-page-supplier-label">{t('inventory:items.filter.supplier')}</InputLabel>
          <Select labelId="item-list-page-supplier-label"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            label={t('inventory:items.filter.supplier')}
          >
            <MenuItem value="">{t('inventory:items.filter.all_suppliers')}</MenuItem>
            {suppliers.map((s) => (
              <MenuItem key={s.supplier_id} value={s.supplier_id}>
                {s.code} · {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>}

        <ToggleButtonGroup
          size="small"
          exclusive
          value={activeStatus}
          onChange={(_, val) => { if (val !== null) setActiveStatus(val); }}
        >
          <ToggleButton value="all">{t('inventory:items.filter.status_all')}</ToggleButton>
          <ToggleButton value="active">{t('common:status.active')}</ToggleButton>
          <ToggleButton value="inactive">{t('common:status.inactive')}</ToggleButton>
        </ToggleButtonGroup>

        {hasActiveFilters && (
          <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
            {t('inventory:items.filter.clear_filters')}
          </Button>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <DataGrid
          rows={items}
          columns={columns}
          getRowId={(row) => row.item_id}
          getRowClassName={(params) => (!params.row.active ? 'row-inactive' : '')}
          loading={loading}
          onRowClick={(params) => navigate(`${params.row.item_id}`)}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          sx={{
            '& .row-inactive': { opacity: 0.5 },
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': {
              minHeight: 'unset !important',
              maxHeight: 'none !important',
              height: 'auto !important',
              display: 'flex !important',
              alignItems: 'center !important',
              padding: '8px 16px !important',
            },
            minHeight: 400,
          }}
        />
      </Box>

      {canAdjustPrices && (
        <PriceAdjustmentDialog
          open={priceDialogOpen}
          onClose={() => setPriceDialogOpen(false)}
          onSuccess={(result) => showSuccess(t('inventory:items.price_adjustment.success', { count: result.adjusted }))}
          productGroups={productGroups}
          initialGroupId={productGroupId}
        />
      )}

      <ConfirmDialog />
    </Box>
  );
}
